import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import toast, { Toaster } from "react-hot-toast";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  LayoutGrid,
  List,
  RefreshCw,
  Search,
  Table2,
  Users,
  X,
  Zap,
  Settings2,
  BarChart3,
  Check,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { db, auth } from "../firebase";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { ATTENDANCE_BRANCHES, branchRowForLabel, usesStringIsoTimestamp } from "../config/attendanceBranches";
import { getVisibleAttendanceBranches } from "../config/appBranches";
import { resolveCollectionName } from "../services/attendanceCollections";
import {
  collectPunchTimes,
  mergeAndDedupeTimestamps,
  computeSessionsFromTimestamps,
  formatDurationHMM,
  attributedMsInDay,
  otFromAttributedMs,
  extractEmpKeyFromData,
  pickNumber,
  pickString,
  hourlyRateFor,
  rateBasisFor,
  toLocalIsoBoundary,
} from "../helpers/staffDailyHours";
import {
  applyBreakDeduction,
  actualFirstLast,
  expectedWindowFromDay,
  resolveExpectedShiftStrings,
  formatClock,
  compareRosterToActual,
  startOfWeekMonday,
  addDays,
  ymdKey,
} from "../helpers/dailyHoursPayroll";
import {
  buildEmployeeCodeLookupForShop,
  extractEmpKeyWithCutover,
} from "../helpers/payrollAttendanceCore";

const DUPLICATE_THRESHOLD_SEC = 10;
const OT_THRESHOLD_MS = 9 * 3600000;
const QUERY_LIMIT = 2000;
/** Match Flutter: prefer CompanyId == "1", then int, then all employees. */
const COMPANY_ID_FILTER = "1";

const LS_PAYROLL_SETTINGS = "dailyHoursPayrollSettings_v1";
const FIRESTORE_APPROVALS = "dailyHoursApprovals";

const DEFAULT_PAYROLL_SETTINGS = {
  breakMinutesPerDay: 0,
  breakMinutesPerSession: 0,
  defaultShiftStart: "09:30",
  defaultShiftEnd: "18:00",
  rosterOverrides: {},
};

function loadPayrollSettings() {
  try {
    const raw = localStorage.getItem(LS_PAYROLL_SETTINGS);
    if (!raw) return { ...DEFAULT_PAYROLL_SETTINGS, rosterOverrides: {} };
    const p = JSON.parse(raw);
    return {
      ...DEFAULT_PAYROLL_SETTINGS,
      ...p,
      rosterOverrides: typeof p.rosterOverrides === "object" && p.rosterOverrides ? p.rosterOverrides : {},
    };
  } catch {
    return { ...DEFAULT_PAYROLL_SETTINGS, rosterOverrides: {} };
  }
}

function branchKeyForDoc(label) {
  const s = String(label || "branch")
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "")
    .slice(0, 96);
  return s || "branch";
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatDateTimeChip(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

function formatSessionRange(start, end) {
  return `${start.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })} → ${end.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })}`;
}

async function fetchDayPunchDocs(punchCollection, queryStart, queryEnd, preferString) {
  const tryGet = async (q) => {
    try {
      return await getDocs(q);
    } catch {
      return null;
    }
  };

  const strQ = () =>
    query(
      collection(db, punchCollection),
      where("timestamp", ">=", toLocalIsoBoundary(queryStart)),
      where("timestamp", "<", toLocalIsoBoundary(queryEnd)),
      orderBy("timestamp"),
      limit(QUERY_LIMIT)
    );

  const tsQ = () =>
    query(
      collection(db, punchCollection),
      where("timestamp", ">=", Timestamp.fromDate(queryStart)),
      where("timestamp", "<", Timestamp.fromDate(queryEnd)),
      orderBy("timestamp"),
      limit(QUERY_LIMIT)
    );

  const order = preferString ? [strQ, tsQ] : [tsQ, strQ];
  for (const build of order) {
    const snap = await tryGet(build());
    if (snap && !snap.empty) return { docs: snap.docs, queryKind: "range" };
  }

  const fallback = await tryGet(query(collection(db, punchCollection), limit(QUERY_LIMIT)));
  if (!fallback) return { docs: [], queryKind: "none" };
  const startMs = queryStart.getTime();
  const endMs = queryEnd.getTime();
  const docs = fallback.docs.filter((d) => {
    const times = collectPunchTimes(d.data());
    return times.some((t) => t.getTime() >= startMs && t.getTime() < endMs);
  });
  return { docs, queryKind: "client-filter" };
}

function buildRowsFromDocs(docs, employeeMeta, startOfDay, endOfDay, employeeCodeLookup) {
  const byEmployee = new Map();
  for (const d of docs) {
    const data = d.data() || {};
    const empId = extractEmpKeyWithCutover(data, d.id, startOfDay, employeeCodeLookup);
    if (!empId) continue;
    const times = collectPunchTimes(data);
    if (times.length === 0) continue;
    if (!byEmployee.has(empId)) byEmployee.set(empId, []);
    byEmployee.get(empId).push(...times);
  }

  const rows = [];
  for (const [empId, raw] of byEmployee) {
    const merged = mergeAndDedupeTimestamps(raw, DUPLICATE_THRESHOLD_SEC);
    const res = computeSessionsFromTimestamps(merged);
    const sessions = res.sessions;
    const attributedMs = attributedMsInDay(sessions, startOfDay, endOfDay);
    const otMs = otFromAttributedMs(attributedMs);
    const meta = employeeMeta[empId];
    const name =
      meta?.name ||
      pickString(meta?.raw || {}, ["EmployeeName", "employeeName", "name"]) ||
      empId;
    rows.push({
      empId,
      name: name || empId,
      merged,
      sessions,
      res,
      attributedMs,
      otMs,
      partial: res.partial,
      totalPairedMs: res.total,
    });
  }
  rows.sort((a, b) => a.name.localeCompare(b.name));
  return rows;
}

function ApprovalRow({ r, persistApproval }) {
  const [reason, setReason] = useState(() => r.approval?.reason || "");
  const [ovMin, setOvMin] = useState(() =>
    r.approval?.paidMsOverride != null ? String(Math.round(Number(r.approval.paidMsOverride) / 60000)) : ""
  );
  useEffect(() => {
    setReason(r.approval?.reason || "");
    setOvMin(r.approval?.paidMsOverride != null ? String(Math.round(Number(r.approval.paidMsOverride) / 60000)) : "");
  }, [r.empId, r.approval?.reason, r.approval?.paidMsOverride]);

  return (
    <div className="flex flex-wrap items-center gap-1 mt-1 text-[9px] border-t border-slate-100 pt-1">
      <label className="inline-flex items-center gap-0.5 cursor-pointer">
        <input
          type="checkbox"
          checked={!!r.approval?.approved}
          onChange={(e) => persistApproval(r.empId, { approved: e.target.checked })}
        />
        Approve
      </label>
      <label className="inline-flex items-center gap-0.5 cursor-pointer">
        <input
          type="checkbox"
          checked={!!r.approval?.locked}
          onChange={(e) => persistApproval(r.empId, { locked: e.target.checked })}
        />
        Lock paid
      </label>
      <input
        type="number"
        min={0}
        className="w-14 border border-slate-200 rounded px-0.5 text-[9px]"
        placeholder="min"
        title="Override paid minutes (when locked)"
        value={ovMin}
        onChange={(e) => setOvMin(e.target.value)}
      />
      <input
        type="text"
        className="flex-1 min-w-[5rem] border border-slate-200 rounded px-1 text-[9px]"
        placeholder="Reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <button
        type="button"
        className="font-semibold text-indigo-700 px-1"
        onClick={() => {
          const n = ovMin.trim() === "" ? null : Number(ovMin);
          persistApproval(r.empId, {
            reason: reason.trim(),
            paidMsOverride: r.approval?.locked && n != null && Number.isFinite(n) ? n * 60000 : null,
          });
        }}
      >
        Save
      </button>
    </div>
  );
}

function SessionTimeline({ sessions, startOfDay }) {
  const dayStart = new Date(startOfDay);
  dayStart.setHours(6, 0, 0, 0);
  const dayEnd = new Date(startOfDay);
  dayEnd.setHours(22, 0, 0, 0);
  const span = Math.max(1, dayEnd - dayStart);
  if (!sessions?.length) {
    return <p className="text-[9px] text-slate-500 mt-1">No sessions for timeline</p>;
  }
  return (
    <div className="mt-1.5">
      <p className="text-[9px] font-semibold text-slate-600 mb-0.5">Timeline (6:00–22:00)</p>
      <div className="relative h-5 w-full rounded bg-slate-100 overflow-hidden border border-slate-200">
        {sessions.map((s, i) => {
          const l = Math.max(0, Math.min(1, (s.start - dayStart) / span));
          const w = Math.max(0.008, Math.min(1 - l, (s.end - s.start) / span));
          return (
            <div
              key={i}
              className="absolute top-0.5 bottom-0.5 bg-indigo-500 rounded-sm min-w-[2px]"
              style={{ left: `${l * 100}%`, width: `${w * 100}%` }}
              title={formatSessionRange(s.start, s.end)}
            />
          );
        })}
      </div>
    </div>
  );
}

function PunchDetailPanel({
  r,
  employeeMeta,
  fmtInr,
  paidMs,
  paidOtMs,
  rosterWindow,
  shift,
  roster,
  startOfDay,
}) {
  const meta = employeeMeta[r.empId];
  const rate = hourlyRateFor(meta);
  const basis = rateBasisFor(meta);
  const amount = rate * (paidMs / 3600000);
  const otStr = paidOtMs > 0 ? formatDurationHMM(paidOtMs) : null;
  const expLabel =
    shift?.startStr && shift?.endStr
      ? `${shift.startStr}–${shift.endStr}`
      : shift?.startStr || shift?.endStr
        ? `${shift.startStr || "?"}–${shift.endStr || "?"}`
        : null;
  return (
    <div className="px-2 pb-2 pt-2 border-t border-slate-200 bg-slate-50/90 text-[10px] leading-snug">
      {expLabel && rosterWindow && (
        <div className="mb-1.5 text-[9px] text-slate-700">
          <span className="font-semibold text-slate-800">Expected:</span> {formatClock(rosterWindow.expStart)} –{" "}
          {formatClock(rosterWindow.expEnd)}
          {r.actualFirst && r.actualLast && (
            <span className="text-slate-600">
              {" "}
              · <span className="font-semibold">Actual:</span> {formatClock(r.actualFirst)} – {formatClock(r.actualLast)}
            </span>
          )}
          {roster?.parts?.length > 0 && (
            <span className="text-amber-800 font-medium"> · {roster.parts.join(" · ")}</span>
          )}
        </div>
      )}
      <SessionTimeline sessions={r.sessions} startOfDay={startOfDay} />
      <div className="flex flex-wrap gap-0.5 py-0.5 mt-1">
        {r.merged.map((p, idx) => (
          <span
            key={`${p.getTime()}-${idx}`}
            className="inline-block px-1.5 py-0.5 rounded bg-white border border-slate-200 font-mono text-[9px]"
          >
            {formatDateTimeChip(p)}
          </span>
        ))}
      </div>
      <p className="font-bold text-slate-800 mb-0.5 mt-1.5 text-[10px]">Sessions</p>
      <ul className="space-y-0.5 mb-1.5">
        {r.sessions.map((s, idx) => {
          const dur = s.end - s.start;
          return (
            <li
              key={idx}
              className="flex items-center justify-between gap-1.5 bg-white rounded px-1.5 py-1 border border-slate-100"
            >
              <span className="font-mono text-[9px]">{formatSessionRange(s.start, s.end)}</span>
              <span className="font-bold tabular-nums text-[10px]">{formatDurationHMM(dur)}</span>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap gap-1.5 text-[10px] font-semibold text-slate-800">
        <span>Total (paired): {formatDurationHMM(r.totalPairedMs)}</span>
        <span>Attributed: {formatDurationHMM(r.attributedMs)}</span>
        <span className="text-indigo-800">Paid (net): {formatDurationHMM(paidMs)}</span>
        {rate > 0 && <span>• {fmtInr.format(amount)}</span>}
      </div>
      {(rate > 0 || otStr) && (
        <div className="mt-0.5 text-[10px] text-slate-600">
          {rate > 0 && (
            <span>
              Rate: {fmtInr.format(rate)} / {basis}
            </span>
          )}
          {otStr && (
            <span className="text-red-600 font-bold ml-1.5 inline-flex items-center gap-0.5">
              <Zap className="w-2.5 h-2.5" aria-hidden />
              OT (on paid): {otStr}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function DailyHoursPage() {
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const visibleBranches = useMemo(() => getVisibleAttendanceBranches(storedUser), [storedUser]);

  const [selectedShopLabel, setSelectedShopLabel] = useState(ATTENDANCE_BRANCHES[0].label);
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  });
  const [nightWindow, setNightWindow] = useState(false);

  const [empCollectionName, setEmpCollectionName] = useState(null);
  const [punchCollectionName, setPunchCollectionName] = useState(null);
  const [employeeMeta, setEmployeeMeta] = useState({});
  const [employeeCodeLookup, setEmployeeCodeLookup] = useState(() => new Map());

  const [loadingPunches, setLoadingPunches] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [openEmpId, setOpenEmpId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [viewMode, setViewMode] = useState("list");

  const [payrollSettings, setPayrollSettings] = useState(loadPayrollSettings);
  const [approvals, setApprovals] = useState({});
  const [weekTotals, setWeekTotals] = useState([]);
  const [weekLoading, setWeekLoading] = useState(false);
  const [payrollPanelOpen, setPayrollPanelOpen] = useState(false);
  const [exportApprovedOnly, setExportApprovedOnly] = useState(false);
  const [rosterJsonDraft, setRosterJsonDraft] = useState("{}");

  const { startOfDay, endOfDay, queryStart, queryEnd } = useMemo(() => {
    const y = selectedDate.getFullYear();
    const m = selectedDate.getMonth();
    const day = selectedDate.getDate();
    const sod = new Date(y, m, day, 0, 0, 0, 0);
    const eod = new Date(y, m, day + 1, 0, 0, 0, 0);
    const qs = nightWindow ? new Date(sod.getTime() - 12 * 3600000) : sod;
    const qe = nightWindow ? new Date(eod.getTime() + 12 * 3600000) : eod;
    return { startOfDay: sod, endOfDay: eod, queryStart: qs, queryEnd: qe };
  }, [selectedDate, nightWindow]);

  useEffect(() => {
    if (!visibleBranches.length) return;
    const allowed = new Set(visibleBranches.map((b) => b.label));
    if (!allowed.has(selectedShopLabel)) {
      setSelectedShopLabel(visibleBranches[0].label);
    }
  }, [visibleBranches, selectedShopLabel]);

  const resolveBranches = useCallback(async () => {
    const row = branchRowForLabel(selectedShopLabel);
    const [ec, pc] = await Promise.all([
      resolveCollectionName(row.employeeCandidates || []),
      resolveCollectionName(row.punchCandidates || []),
    ]);
    setEmpCollectionName(ec);
    setPunchCollectionName(pc);
  }, [selectedShopLabel]);

  useEffect(() => {
    resolveBranches();
  }, [resolveBranches]);

  const loadEmployees = useCallback(async () => {
    if (!empCollectionName) return;
    try {
      let snap = await getDocs(
        query(collection(db, empCollectionName), where("CompanyId", "==", COMPANY_ID_FILTER))
      );
      if (snap.empty) {
        const intVal = parseInt(COMPANY_ID_FILTER, 10);
        if (!isNaN(intVal)) {
          snap = await getDocs(
            query(collection(db, empCollectionName), where("CompanyId", "==", intVal))
          );
        }
      }
      if (snap.empty) {
        snap = await getDocs(collection(db, empCollectionName));
      }
      const map = {};
      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const key = extractEmpKeyFromData(data, docSnap.id);
        const name =
          pickString(data, [
            "EmployeeName",
            "employeeName",
            "Employee",
            "name",
            "fullName",
            "fullname",
            "displayName",
            "display_name",
          ]) || key;
        map[key] = {
          name: name || key,
          salaryPerHour: pickNumber(data, ["salaryPerHour", "SalaryPerHour", "hourlySalary", "HourlySalary"]),
          dailySalary: pickNumber(data, ["dailySalary", "DailySalary", "salaryPerDay", "SalaryPerDay"]),
          monthlySalary: pickNumber(data, ["monthlySalary", "MonthlySalary", "salary", "Salary"]),
          raw: data,
        };
      });
      setEmployeeMeta(map);
      try {
        setEmployeeCodeLookup(buildEmployeeCodeLookupForShop({ label: selectedShopLabel }, snap.docs));
      } catch {
        setEmployeeCodeLookup(new Map());
      }
    } catch (e) {
      console.error(e);
      setEmployeeMeta({});
      setEmployeeCodeLookup(new Map());
    }
  }, [empCollectionName, selectedShopLabel]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_PAYROLL_SETTINGS, JSON.stringify(payrollSettings));
    } catch {
      /* ignore */
    }
  }, [payrollSettings]);

  useEffect(() => {
    if (payrollPanelOpen) {
      setRosterJsonDraft(JSON.stringify(payrollSettings.rosterOverrides || {}, null, 2));
    }
  }, [payrollPanelOpen, payrollSettings.rosterOverrides]);

  const breakOpts = useMemo(
    () => ({
      breakMinutesPerDay: payrollSettings.breakMinutesPerDay,
      breakMinutesPerSession: payrollSettings.breakMinutesPerSession,
    }),
    [payrollSettings.breakMinutesPerDay, payrollSettings.breakMinutesPerSession]
  );

  const loadPunches = useCallback(async () => {
    if (!punchCollectionName) {
      setRows([]);
      return;
    }
    setLoadingPunches(true);
    setError("");
    try {
      const preferString = usesStringIsoTimestamp(punchCollectionName);
      const { docs } = await fetchDayPunchDocs(punchCollectionName, queryStart, queryEnd, preferString);
      const built = buildRowsFromDocs(docs, employeeMeta, startOfDay, endOfDay, employeeCodeLookup);
      setRows(built);
    } catch (e) {
      console.error(e);
      setError(e?.message || "Failed to load punches");
      setRows([]);
    } finally {
      setLoadingPunches(false);
    }
  }, [punchCollectionName, queryStart, queryEnd, startOfDay, endOfDay, employeeMeta, employeeCodeLookup]);

  useEffect(() => {
    loadPunches();
  }, [loadPunches]);

  const enrichedRows = useMemo(() => {
    return rows.map((r) => {
      const paidMs = applyBreakDeduction(r.attributedMs, r.sessions.length, breakOpts);
      const paidOtMs = otFromAttributedMs(paidMs);
      const shift = resolveExpectedShiftStrings(
        r.empId,
        employeeMeta[r.empId],
        {
          defaultShiftStart: payrollSettings.defaultShiftStart,
          defaultShiftEnd: payrollSettings.defaultShiftEnd,
        },
        payrollSettings.rosterOverrides || {}
      );
      const win =
        shift.startMin != null && shift.endMin != null
          ? expectedWindowFromDay(startOfDay, shift.startMin, shift.endMin)
          : null;
      const { first, last } = actualFirstLast(r.sessions);
      const roster = compareRosterToActual(win, first, last);
      return {
        ...r,
        paidMs,
        paidOtMs,
        shift,
        rosterWindow: win,
        roster,
        actualFirst: first,
        actualLast: last,
      };
    });
  }, [
    rows,
    breakOpts,
    employeeMeta,
    payrollSettings.defaultShiftStart,
    payrollSettings.defaultShiftEnd,
    payrollSettings.rosterOverrides,
    startOfDay,
  ]);

  const effectiveRows = useMemo(() => {
    return enrichedRows.map((r) => {
      const a = approvals[r.empId];
      let paid = r.paidMs;
      if (a?.locked && a.paidMsOverride != null && Number.isFinite(Number(a.paidMsOverride))) {
        paid = Number(a.paidMsOverride);
      }
      const effectivePaidOtMs = otFromAttributedMs(paid);
      return { ...r, effectivePaidMs: paid, effectivePaidOtMs, approval: a };
    });
  }, [enrichedRows, approvals]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ref = doc(db, FIRESTORE_APPROVALS, `${branchKeyForDoc(selectedShopLabel)}_${ymdKey(selectedDate)}`);
        const snap = await getDoc(ref);
        if (cancelled) return;
        setApprovals(snap.exists() ? snap.data().entries || {} : {});
      } catch (e) {
        console.error(e);
        if (!cancelled) setApprovals({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedShopLabel, selectedDate]);

  const persistApproval = useCallback(
    async (empId, patch) => {
      try {
        const ref = doc(db, FIRESTORE_APPROVALS, `${branchKeyForDoc(selectedShopLabel)}_${ymdKey(selectedDate)}`);
        const snap = await getDoc(ref);
        const prev = snap.exists() ? snap.data().entries || {} : {};
        const uid = auth.currentUser?.uid || auth.currentUser?.email || "web";
        const merged = {
          ...prev[empId],
          ...patch,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        };
        await setDoc(
          ref,
          {
            branch: selectedShopLabel,
            dateKey: ymdKey(selectedDate),
            entries: { ...prev, [empId]: merged },
          },
          { merge: true }
        );
        setApprovals((p) => ({ ...p, [empId]: { ...p[empId], ...patch } }));
        toast.success("Approval saved");
      } catch (e) {
        console.error(e);
        toast.error(e?.message || "Could not save approval (Firestore rules?)");
      }
    },
    [selectedShopLabel, selectedDate]
  );

  const loadWeekTotals = useCallback(async () => {
    if (!punchCollectionName) {
      setWeekTotals([]);
      return;
    }
    setWeekLoading(true);
    try {
      const preferString = usesStringIsoTimestamp(punchCollectionName);
      const monday = startOfWeekMonday(selectedDate);
      const opts = {
        breakMinutesPerDay: payrollSettings.breakMinutesPerDay,
        breakMinutesPerSession: payrollSettings.breakMinutesPerSession,
      };
      const promises = [];
      for (let i = 0; i < 7; i++) {
        const day = addDays(monday, i);
        const y = day.getFullYear();
        const m = day.getMonth();
        const da = day.getDate();
        const sod = new Date(y, m, da, 0, 0, 0, 0);
        const eod = new Date(y, m, da + 1, 0, 0, 0, 0);
        const qs = nightWindow ? new Date(sod.getTime() - 12 * 3600000) : sod;
        const qe = nightWindow ? new Date(eod.getTime() + 12 * 3600000) : eod;
        promises.push(
          fetchDayPunchDocs(punchCollectionName, qs, qe, preferString).then(({ docs }) => {
            const built = buildRowsFromDocs(docs, employeeMeta, sod, eod, employeeCodeLookup);
            let total = 0;
            for (const row of built) {
              total += applyBreakDeduction(row.attributedMs, row.sessions.length, opts);
            }
            return { day: sod, totalMs: total, key: ymdKey(sod) };
          })
        );
      }
      const result = await Promise.all(promises);
      setWeekTotals(result);
    } catch (e) {
      console.error(e);
      setWeekTotals([]);
    } finally {
      setWeekLoading(false);
    }
  }, [
    punchCollectionName,
    selectedDate,
    nightWindow,
    employeeMeta,
    employeeCodeLookup,
    payrollSettings.breakMinutesPerDay,
    payrollSettings.breakMinutesPerSession,
  ]);

  useEffect(() => {
    loadWeekTotals();
  }, [loadWeekTotals]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = effectiveRows;
    if (q) {
      list = effectiveRows.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          String(r.empId).toLowerCase().includes(q)
      );
    }
    if (exportApprovedOnly) {
      list = list.filter((r) => r.approval?.approved === true);
    }
    return list;
  }, [effectiveRows, search, exportApprovedOnly]);

  const sortedRows = useMemo(() => {
    const arr = [...filtered];
    if (sortBy === "hours") {
      arr.sort((a, b) => b.effectivePaidMs - a.effectivePaidMs || a.name.localeCompare(b.name));
    } else {
      arr.sort((a, b) => a.name.localeCompare(b.name));
    }
    return arr;
  }, [filtered, sortBy]);

  const chartData = useMemo(
    () =>
      sortedRows.slice(0, 24).map((r) => ({
        name: r.name.length > 14 ? `${r.name.slice(0, 13)}…` : r.name,
        hours: Math.round((r.effectivePaidMs / 3600000) * 100) / 100,
      })),
    [sortedRows]
  );

  const { grandMs, grandOtMs } = useMemo(() => {
    let g = 0;
    let go = 0;
    for (const r of filtered) {
      g += r.effectivePaidMs;
      go += r.effectivePaidOtMs;
    }
    return { grandMs: g, grandOtMs: go };
  }, [filtered]);

  const grandEstimatedInr = useMemo(() => {
    let t = 0;
    for (const r of filtered) {
      const rate = hourlyRateFor(employeeMeta[r.empId]);
      if (rate > 0) t += rate * (r.effectivePaidMs / 3600000);
    }
    return t;
  }, [filtered, employeeMeta]);

  const dateStrLong = useMemo(
    () =>
      selectedDate.toLocaleDateString("en-IN", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    [selectedDate]
  );

  const fmtInr = useMemo(
    () => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }),
    []
  );

  const exportExcel = () => {
    if (!filtered.length) {
      toast.error("No data to export");
      return;
    }
    setExporting(true);
    try {
      const dailyData = [
        [
          "Employee ID",
          "Employee Name",
          "Attributed",
          "Paid (after breaks)",
          "Regular (paid)",
          "OT (on paid)",
          "Hourly Rate (₹)",
          "Amount (₹)",
          "Approved",
          "Reason",
        ],
      ];
      let totalAmount = 0;
      for (const emp of filtered) {
        const meta = employeeMeta[emp.empId];
        const rate = hourlyRateFor(meta);
        const amount = rate * (emp.effectivePaidMs / 3600000);
        totalAmount += amount;
        dailyData.push([
          emp.empId,
          emp.name,
          formatDurationHMM(emp.attributedMs),
          formatDurationHMM(emp.effectivePaidMs),
          formatDurationHMM(Math.max(0, emp.effectivePaidMs - emp.effectivePaidOtMs)),
          emp.effectivePaidOtMs > 0 ? formatDurationHMM(emp.effectivePaidOtMs) : "0:00",
          rate,
          amount,
          emp.approval?.approved ? "Yes" : "No",
          emp.approval?.reason || "",
        ]);
      }
      const wb = XLSX.utils.book_new();
      const ws1 = XLSX.utils.aoa_to_sheet(dailyData);
      XLSX.utils.book_append_sheet(wb, ws1, "Staff Daily Hours");

      const summary = [
        ["Field", "Value"],
        ["Shop", selectedShopLabel],
        ["Date", dateStrLong],
        ["Total Employees", filtered.length],
        ["Break min/day", payrollSettings.breakMinutesPerDay],
        ["Break min/session", payrollSettings.breakMinutesPerSession],
        ["Default shift", `${payrollSettings.defaultShiftStart}–${payrollSettings.defaultShiftEnd}`],
        ["Export filter", exportApprovedOnly ? "Approved only" : "All rows"],
        ["Total paid hours", formatDurationHMM(grandMs)],
        ["Total OT (on paid)", formatDurationHMM(grandOtMs)],
        ["Total Amount (₹)", totalAmount],
        ["OT Threshold", "9 hours (on paid time)"],
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(summary);
      XLSX.utils.book_append_sheet(wb, ws2, "Summary");

      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const shopFile = selectedShopLabel.replace(/\s+/g, "_").replace(/[^\w_-]/g, "");
      const dateFile = `${selectedDate.getFullYear()}-${pad2(selectedDate.getMonth() + 1)}-${pad2(selectedDate.getDate())}`;
      saveAs(
        new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        `staff_daily_hours_${shopFile}_${dateFile}.xlsx`
      );
      toast.success("Export successful");
    } catch (e) {
      console.error(e);
      toast.error(`Export failed: ${e?.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  const changeDay = (delta) => {
    setSelectedDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta));
  };

  const goToday = () => {
    const d = new Date();
    setSelectedDate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
  };

  const isToday = useMemo(() => {
    const t = new Date();
    return (
      selectedDate.getFullYear() === t.getFullYear() &&
      selectedDate.getMonth() === t.getMonth() &&
      selectedDate.getDate() === t.getDate()
    );
  }, [selectedDate]);

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="max-w-5xl mx-auto px-2 sm:px-3 pb-10 pt-1 sm:pt-2">
        <header className="mb-2 sm:mb-3">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900">Daily hours</h1>
              <p className="text-xs text-slate-600 mt-0.5 max-w-xl leading-snug">
                Punch-in/out pairs for the selected day, attributed hours, OT over 9h, and optional pay estimates from employee
                rates.
              </p>
            </div>
          </div>
        </header>

        <div className="rounded-xl border border-slate-200/80 bg-white shadow-sm overflow-hidden text-[13px] sm:text-sm">
          <div className="bg-gradient-to-br from-indigo-700 via-indigo-700 to-indigo-900 text-white px-3 py-2.5 sm:px-4 sm:py-3">
            <div className="flex flex-col lg:flex-row lg:items-center gap-2 sm:gap-3">
              <div className="flex flex-wrap items-center gap-1.5">
                <label className="text-[10px] uppercase tracking-wide text-indigo-200/90 font-semibold">Branch</label>
                <select
                  value={selectedShopLabel}
                  onChange={(e) => setSelectedShopLabel(e.target.value)}
                  className="text-xs text-slate-900 bg-white rounded-md px-2 py-1.5 max-w-[200px] border border-white/20 shadow-sm font-medium"
                >
                  {(visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES).map((b) => (
                    <option key={b.label} value={b.label}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap items-center gap-1.5 lg:ml-auto">
                <button
                  type="button"
                  onClick={() => changeDay(-1)}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                  title="Previous day"
                  aria-label="Previous day"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <label className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-1.5 py-1 cursor-pointer hover:bg-white/15">
                  <CalendarDays className="w-3.5 h-3.5 text-indigo-200 shrink-0" aria-hidden />
                  <input
                    type="date"
                    className="text-slate-900 text-xs font-medium bg-white rounded-md px-1.5 py-1 border-0 max-w-[128px]"
                    value={`${selectedDate.getFullYear()}-${pad2(selectedDate.getMonth() + 1)}-${pad2(selectedDate.getDate())}`}
                    onChange={(e) => {
                      const [y, mo, da] = e.target.value.split("-").map(Number);
                      setSelectedDate(new Date(y, mo - 1, da));
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => changeDay(1)}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                  title="Next day"
                  aria-label="Next day"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={goToday}
                  disabled={isToday}
                  className="text-[10px] font-bold uppercase tracking-wide px-2 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => loadPunches()}
                  disabled={loadingPunches || !punchCollectionName}
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 disabled:opacity-50"
                  title="Reload punches"
                >
                  <RefreshCw className={`w-3 h-3 ${loadingPunches ? "animate-spin" : ""}`} />
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={() => setPayrollPanelOpen(true)}
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-1.5 rounded-lg bg-white/15 hover:bg-white/25"
                  title="Breaks, default shift, roster overrides"
                >
                  <Settings2 className="w-3 h-3" />
                  Payroll
                </button>
                <label className="inline-flex items-center gap-1 text-[9px] font-semibold text-indigo-100 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="rounded border-white/40"
                    checked={exportApprovedOnly}
                    onChange={(e) => setExportApprovedOnly(e.target.checked)}
                  />
                  Export approved only
                </label>
                <button
                  type="button"
                  disabled={exporting}
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-1.5 rounded-lg bg-white text-indigo-800 hover:bg-indigo-50 disabled:opacity-50 shadow-sm"
                  onClick={exportExcel}
                >
                  <Download className="w-3 h-3" />
                  {exporting ? "…" : "Export"}
                </button>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <div className="flex items-center gap-1.5 text-white/95">
                <Clock className="w-3.5 h-3.5 text-indigo-200 shrink-0" aria-hidden />
                <span className="font-semibold">{dateStrLong}</span>
              </div>
              <span className="hidden sm:inline text-indigo-300/80">|</span>
              <div className="flex flex-wrap items-center gap-2 text-[11px] sm:text-xs">
                <span className="font-semibold">{formatDurationHMM(grandMs)} paid total</span>
                <span className="inline-flex items-center gap-0.5 text-amber-200">
                  <Zap className="w-3 h-3" aria-hidden />
                  OT {formatDurationHMM(grandOtMs)}
                </span>
                {grandEstimatedInr > 0 && (
                  <span className="text-emerald-200/95 font-semibold tabular-nums">Est. {fmtInr.format(grandEstimatedInr)}</span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 sm:gap-2 p-2 sm:p-2.5 bg-slate-50 border-b border-slate-100">
            <div className="rounded-lg border border-slate-200/80 bg-white px-2 py-2 shadow-sm">
              <div className="flex items-center gap-1 text-slate-500 text-[10px] font-semibold uppercase tracking-wide">
                <Users className="w-3 h-3" aria-hidden />
                Staff
              </div>
              <p className="text-lg font-bold tabular-nums text-slate-900 mt-0.5 leading-tight">{filtered.length}</p>
            </div>
            <div className="rounded-lg border border-slate-200/80 bg-white px-2 py-2 shadow-sm">
              <div className="flex items-center gap-1 text-slate-500 text-[10px] font-semibold uppercase tracking-wide">
                <Clock className="w-3 h-3" aria-hidden />
                Hours
              </div>
              <p className="text-lg font-bold tabular-nums text-slate-900 mt-0.5 leading-tight">{formatDurationHMM(grandMs)}</p>
            </div>
            <div className="rounded-lg border border-amber-200/80 bg-amber-50/80 px-2 py-2 shadow-sm">
              <div className="flex items-center gap-1 text-amber-800/90 text-[10px] font-semibold uppercase tracking-wide">
                <Zap className="w-3 h-3" aria-hidden />
                OT
              </div>
              <p className="text-lg font-bold tabular-nums text-amber-950 mt-0.5 leading-tight">{formatDurationHMM(grandOtMs)}</p>
            </div>
            <div className="rounded-lg border border-emerald-200/80 bg-emerald-50/50 px-2 py-2 shadow-sm col-span-2 sm:col-span-1">
              <div className="text-emerald-800/90 text-[10px] font-semibold uppercase tracking-wide">Est. pay (day)</div>
              <p className="text-base sm:text-lg font-bold tabular-nums text-emerald-900 mt-0.5 leading-tight">
                {grandEstimatedInr > 0 ? fmtInr.format(grandEstimatedInr) : "—"}
              </p>
            </div>
          </div>

          <div className="px-2 py-2 border-b border-slate-100 bg-slate-50/50">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">Week — paid hours (after breaks)</span>
              {weekLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-500" aria-hidden /> : null}
            </div>
            <div className="flex gap-1 overflow-x-auto pb-0.5">
              {weekTotals.map((w) => {
                const sel = ymdKey(selectedDate) === w.key;
                return (
                  <button
                    key={w.key}
                    type="button"
                    onClick={() => setSelectedDate(new Date(w.day.getFullYear(), w.day.getMonth(), w.day.getDate()))}
                    className={`shrink-0 rounded-lg border px-2 py-1 text-left min-w-[4.5rem] transition-colors ${
                      sel ? "border-indigo-600 bg-indigo-50 ring-1 ring-indigo-300" : "border-slate-200 bg-white hover:bg-slate-50"
                    }`}
                  >
                    <div className="text-[9px] text-slate-500 leading-tight">
                      {w.day.toLocaleDateString("en-IN", { weekday: "short" })}
                    </div>
                    <div className="text-[10px] font-bold tabular-nums text-slate-900">{formatDurationHMM(w.totalMs)}</div>
                    <div className="text-[8px] text-slate-400 tabular-nums">{pad2(w.day.getDate())}/{pad2(w.day.getMonth() + 1)}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-2.5 sm:p-3">
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 mb-2">
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  checked={nightWindow}
                  onChange={(e) => setNightWindow(e.target.checked)}
                />
                <span>
                  ±12h query window <span className="text-slate-500 text-[10px]">(night shift)</span>
                </span>
              </label>

              <div className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Sort</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-xs border border-slate-200 rounded-md px-1.5 py-1 bg-white text-slate-800"
                >
                  <option value="name">Name (A–Z)</option>
                  <option value="hours">Hours (high → low)</option>
                </select>
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide hidden sm:inline">View</span>
                <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50">
                  <button
                    type="button"
                    onClick={() => setViewMode("list")}
                    className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                      viewMode === "list" ? "bg-white shadow text-indigo-800" : "text-slate-600 hover:text-slate-900"
                    }`}
                    title="Card list"
                  >
                    <List className="w-3 h-3" />
                    List
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("table")}
                    className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                      viewMode === "table" ? "bg-white shadow text-indigo-800" : "text-slate-600 hover:text-slate-900"
                    }`}
                    title="Table"
                  >
                    <Table2 className="w-3 h-3" />
                    Table
                  </button>
                </div>
              </div>
            </div>

            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or employee ID…"
                className="w-full border border-slate-200 rounded-lg pl-8 pr-9 py-1.5 text-xs bg-white shadow-inner focus:ring-1 focus:ring-indigo-500/30 focus:border-indigo-400 outline-none"
                aria-label="Search employees"
              />
              {search.trim() ? (
                <button
                  type="button"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              ) : null}
            </div>

            {sortedRows.length > 0 && (
              <div className="mb-2 rounded-lg border border-slate-200 p-2 bg-white">
                <div className="flex items-center gap-1 text-[10px] font-semibold text-slate-600 mb-1">
                  <BarChart3 className="w-3.5 h-3.5" aria-hidden />
                  Paid hours (top 24, after breaks & locks)
                </div>
                <div className="h-44 w-full min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" height={48} interval={0} />
                      <YAxis tick={{ fontSize: 9 }} width={32} />
                      <Tooltip formatter={(v) => [`${v} h`, "Paid"]} />
                      <Bar dataKey="hours" fill="#4f46e5" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-800" role="alert">
                {error}
              </div>
            )}

            {!empCollectionName || !punchCollectionName ? (
              <div className="flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2.5">
                <LayoutGrid className="w-4 h-4 shrink-0 animate-pulse" />
                <p className="text-xs font-medium">Resolving Firestore collections for this branch…</p>
              </div>
            ) : loadingPunches ? (
              <div className="space-y-1.5">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 px-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/50">
                <p className="font-semibold text-slate-800 text-sm">No punch records for {dateStrLong}</p>
                <p className="text-xs text-slate-600 mt-0.5">{selectedShopLabel}</p>
                {search.trim() ? (
                  <p className="text-xs text-slate-500 mt-2">Try clearing the search or pick another date.</p>
                ) : (
                  <p className="text-xs text-slate-500 mt-2">Check branch selection or night-shift window if punches are missing.</p>
                )}
              </div>
            ) : viewMode === "table" ? (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs text-left">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-600">
                      <th className="px-2 py-1.5 font-semibold">Employee</th>
                      <th className="px-2 py-1.5 font-semibold">ID</th>
                      <th className="px-2 py-1.5 font-semibold text-right">Paid</th>
                      <th className="px-2 py-1.5 font-semibold text-right">Attr</th>
                      <th className="px-2 py-1.5 font-semibold text-right">OT</th>
                      <th className="px-2 py-1.5 font-semibold text-right">₹</th>
                      <th className="px-2 py-1.5 font-semibold text-center">OK</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((r) => {
                      const meta = employeeMeta[r.empId];
                      const rate = hourlyRateFor(meta);
                      const amount = rate * (r.effectivePaidMs / 3600000);
                      const otStr = r.effectivePaidOtMs > 0 ? formatDurationHMM(r.effectivePaidOtMs) : "—";
                      return (
                        <tr
                          key={r.empId}
                          className="border-b border-slate-100 hover:bg-indigo-50/40 cursor-pointer"
                          onClick={() => setOpenEmpId(openEmpId === r.empId ? null : r.empId)}
                        >
                          <td className="px-2 py-1.5 font-medium text-slate-900">
                            <div className="flex items-center gap-1.5">
                              <span className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-700">
                                {(r.name || "?").slice(0, 1).toUpperCase()}
                              </span>
                              <span className="truncate max-w-[160px] sm:max-w-[200px]">{r.name}</span>
                            </div>
                          </td>
                          <td className="px-2 py-1.5 font-mono text-[10px] text-slate-600">{r.empId}</td>
                          <td className="px-2 py-1.5 text-right font-bold tabular-nums">{formatDurationHMM(r.effectivePaidMs)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{formatDurationHMM(r.attributedMs)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-amber-800/90">{otStr}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-emerald-800/90">
                            {rate > 0 ? fmtInr.format(amount) : "—"}
                          </td>
                          <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="rounded border-slate-300"
                              checked={!!r.approval?.approved}
                              onChange={(e) => persistApproval(r.empId, { approved: e.target.checked })}
                              title="Approved"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-[10px] text-slate-500 px-2 py-1.5 bg-slate-50 border-t border-slate-100">
                  Click a row for punch times and sessions. Click again to collapse.
                </p>
                {openEmpId
                  ? (() => {
                      const r = sortedRows.find((x) => x.empId === openEmpId);
                      if (!r) return null;
                      return (
                        <div className="border-t border-slate-200 bg-white">
                          <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-slate-100/90 border-b border-slate-200">
                            <span className="text-xs font-semibold text-slate-900 truncate">
                              {r.name}{" "}
                              <span className="font-mono text-[10px] font-normal text-slate-600">({r.empId})</span>
                            </span>
                            <button
                              type="button"
                              className="text-[10px] font-semibold text-indigo-700 hover:underline shrink-0"
                              onClick={() => setOpenEmpId(null)}
                            >
                              Close
                            </button>
                          </div>
                          <PunchDetailPanel
                            r={r}
                            employeeMeta={employeeMeta}
                            fmtInr={fmtInr}
                            paidMs={r.effectivePaidMs}
                            paidOtMs={r.effectivePaidOtMs}
                            rosterWindow={r.rosterWindow}
                            shift={r.shift}
                            roster={r.roster}
                            startOfDay={startOfDay}
                          />
                          <div className="px-2 pb-2" onClick={(e) => e.stopPropagation()}>
                            <ApprovalRow r={r} persistApproval={persistApproval} />
                          </div>
                        </div>
                      );
                    })()
                  : null}
              </div>
            ) : null}

            {filtered.length > 0 && viewMode === "list" ? (
            <ul className="space-y-1.5">
              {sortedRows.map((r) => {
                const meta = employeeMeta[r.empId];
                const rate = hourlyRateFor(meta);
                const amount = rate * (r.effectivePaidMs / 3600000);
                const progress = OT_THRESHOLD_MS === 0 ? 0 : Math.min(1, r.effectivePaidMs / OT_THRESHOLD_MS);
                const otStr = r.effectivePaidOtMs > 0 ? formatDurationHMM(r.effectivePaidOtMs) : null;
                const open = openEmpId === r.empId;
                return (
                  <li
                    key={r.empId}
                    className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm"
                  >
                    <button
                      type="button"
                      className="w-full text-left p-2 sm:p-2.5 hover:bg-slate-50/80 transition-colors"
                      onClick={() => setOpenEmpId(open ? null : r.empId)}
                    >
                      <div className="flex items-start gap-2">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-800 flex items-center justify-center text-xs font-bold shrink-0 border border-indigo-200/60">
                          {(r.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-start justify-between gap-1.5">
                            <div className="min-w-0">
                              <div className="font-bold text-xs text-slate-900 truncate">{r.name}</div>
                              <div className="text-[10px] font-mono text-slate-500 truncate">{r.empId}</div>
                              {r.roster?.parts?.length > 0 && (
                                <div className="text-[9px] text-amber-800 mt-0.5">{r.roster.parts.join(" · ")}</div>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-sm font-bold tabular-nums text-slate-900 leading-tight">
                                {formatDurationHMM(r.effectivePaidMs)}
                              </div>
                              <div className="text-[9px] text-slate-500 tabular-nums">
                                attr {formatDurationHMM(r.attributedMs)}
                              </div>
                              {rate > 0 && (
                                <div className="text-[10px] font-semibold text-emerald-800 tabular-nums">{fmtInr.format(amount)}</div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap mt-1">
                            {otStr && (
                              <span className="text-[10px] font-bold text-red-600 inline-flex items-center gap-0.5">
                                <Zap className="w-3 h-3 shrink-0" aria-hidden />
                                OT {otStr}
                              </span>
                            )}
                            {r.approval?.approved && (
                              <span className="text-[9px] font-semibold text-emerald-700 inline-flex items-center gap-0.5">
                                <Check className="w-3 h-3" aria-hidden />
                                OK
                              </span>
                            )}
                          </div>
                          <div className="mt-1.5 h-1 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-indigo-500 rounded-full transition-all"
                              style={{ width: `${progress * 100}%` }}
                            />
                          </div>
                          <div className="flex flex-wrap gap-0.5 mt-1.5 text-[9px]">
                            <span className="px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-900 border border-indigo-100 font-medium">
                              Punches {r.merged.length}
                            </span>
                            {otStr && (
                              <span className="px-1.5 py-0.5 rounded-full bg-red-50 text-red-800 border border-red-100 font-medium">
                                OT
                              </span>
                            )}
                            {r.partial && (
                              <span className="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-100 font-medium">
                                Partial
                              </span>
                            )}
                            <span className="ml-auto text-slate-400">{open ? "▲" : "▼"}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                    <ApprovalRow r={r} persistApproval={persistApproval} />
                    {open && (
                      <PunchDetailPanel
                        r={r}
                        employeeMeta={employeeMeta}
                        fmtInr={fmtInr}
                        paidMs={r.effectivePaidMs}
                        paidOtMs={r.effectivePaidOtMs}
                        rosterWindow={r.rosterWindow}
                        shift={r.shift}
                        roster={r.roster}
                        startOfDay={startOfDay}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          ) : null}
          </div>
        </div>

        <p className="text-[10px] text-slate-500 mt-2 px-0.5 max-w-4xl leading-snug">
          <strong className="text-slate-600">Note:</strong> OT = time above 9h on <strong>paid</strong> time (after breaks). Approvals stored in Firestore{" "}
          <code className="text-[9px] bg-slate-200/80 px-0.5 rounded">{FIRESTORE_APPROVALS}</code>. Punch{" "}
          <code className="text-[9px] bg-slate-200/80 px-0.5 rounded">{punchCollectionName || "—"}</code>, employees{" "}
          <code className="text-[9px] bg-slate-200/80 px-0.5 rounded">{empCollectionName || "—"}</code>.
        </p>
      </main>

      {payrollPanelOpen && (
        <div
          className="fixed inset-0 z-[1700] flex items-center justify-center p-3 bg-black/60 print:hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payroll-settings-title"
          onClick={() => setPayrollPanelOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between gap-2">
              <h2 id="payroll-settings-title" className="text-sm font-bold text-slate-900">
                Payroll settings
              </h2>
              <button
                type="button"
                className="text-slate-500 hover:text-slate-800 p-1"
                onClick={() => setPayrollPanelOpen(false)}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 space-y-3 text-xs text-slate-700">
              <p className="text-[11px] text-slate-600 leading-snug">
                Breaks reduce <strong>paid</strong> hours before OT. Default shift compares to actual first/last punch; per-employee
                overrides can be set in JSON (employee IDs from your roster).
              </p>
              <label className="block">
                <span className="font-semibold text-slate-800">Break (minutes / day)</span>
                <input
                  type="number"
                  min={0}
                  className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5"
                  value={payrollSettings.breakMinutesPerDay}
                  onChange={(e) =>
                    setPayrollSettings((s) => ({ ...s, breakMinutesPerDay: Math.max(0, Number(e.target.value) || 0) }))
                  }
                />
              </label>
              <label className="block">
                <span className="font-semibold text-slate-800">Break (minutes / session)</span>
                <input
                  type="number"
                  min={0}
                  className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5"
                  value={payrollSettings.breakMinutesPerSession}
                  onChange={(e) =>
                    setPayrollSettings((s) => ({ ...s, breakMinutesPerSession: Math.max(0, Number(e.target.value) || 0) }))
                  }
                />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="font-semibold text-slate-800">Default shift start</span>
                  <input
                    type="text"
                    className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 font-mono"
                    placeholder="09:30"
                    value={payrollSettings.defaultShiftStart}
                    onChange={(e) => setPayrollSettings((s) => ({ ...s, defaultShiftStart: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="font-semibold text-slate-800">Default shift end</span>
                  <input
                    type="text"
                    className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 font-mono"
                    placeholder="18:00"
                    value={payrollSettings.defaultShiftEnd}
                    onChange={(e) => setPayrollSettings((s) => ({ ...s, defaultShiftEnd: e.target.value }))}
                  />
                </label>
              </div>
              <label className="block">
                <span className="font-semibold text-slate-800">Roster overrides (JSON)</span>
                <textarea
                  className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 font-mono text-[10px] min-h-[100px]"
                  placeholder='{"EMP001":{"start":"09:00","end":"17:30"}}'
                  value={rosterJsonDraft}
                  onChange={(e) => setRosterJsonDraft(e.target.value)}
                  spellCheck={false}
                />
              </label>
              <p className="text-[10px] text-slate-500">
                Employee documents can also use fields like ExpectedShiftStart / ExpectedShiftEnd (see code). Week strip reloads when
                you change breaks.
              </p>
              <button
                type="button"
                className="w-full rounded-lg bg-indigo-600 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                onClick={() => {
                  try {
                    const p = JSON.parse(rosterJsonDraft || "{}");
                    setPayrollSettings((s) => ({
                      ...s,
                      rosterOverrides: typeof p === "object" && p && !Array.isArray(p) ? p : {},
                    }));
                  } catch {
                    toast.error("Invalid roster JSON");
                    return;
                  }
                  loadWeekTotals();
                  setPayrollPanelOpen(false);
                  toast.success("Settings saved");
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
